import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ClipboardCheck, Sparkles, Loader2, AlertCircle, CheckCircle2,
  Clock, ShieldAlert, MessageSquare, DollarSign, RotateCw,
  ListChecks, ScrollText, Copy,
} from 'lucide-react';
import '../styles/SessionKitTab.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PROFILES = [
  { id: 'cadence-30', label: 'CADENCE Consult', minutes: 30, statusKey: 'status_cadence_30', dataKey: 'agenda_cadence_30' },
  { id: 'clinic-11',  label: 'In-Clinic (Beta)', minutes: 11, statusKey: 'status_clinic_11',  dataKey: 'agenda_clinic_11'  },
];

const SCOPE_TAG_META = {
  'safety-override':    { label: 'SAFETY',     icon: <ShieldAlert size={14} />, kind: 'safety' },
  'treatment-decision': { label: 'TREATMENT',  icon: <ShieldAlert size={14} />, kind: 'hard' },
  'diagnosis':          { label: 'DIAGNOSIS',  icon: <ShieldAlert size={14} />, kind: 'hard' },
  'dose-decision':      { label: 'DOSE',       icon: <ShieldAlert size={14} />, kind: 'hard' },
  'separate-case':      { label: 'SEPARATE',   icon: <DollarSign size={14} />,  kind: 'upsell' },
  'separate-service':   { label: 'SEPARATE',   icon: <DollarSign size={14} />,  kind: 'upsell' },
  'upsell':             { label: 'UPSELL',     icon: <DollarSign size={14} />,  kind: 'upsell' },
  'time-redirect':      { label: 'TIME',       icon: <Clock size={14} />,        kind: 'time' },
};

const SessionKitTab = ({ caseId, token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeProfile, setActiveProfile] = useState('cadence-30');
  const [generating, setGenerating] = useState(null); // profile id while in flight
  const [banner, setBanner] = useState(null);
  const pollRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const load = useCallback(async () => {
    if (!caseId) { setLoading(false); return; }
    try {
      const res = await fetch(`${API_URL}/api/admin/cases/${caseId}/session-kit`, { headers });
      if (!res.ok) throw new Error(`Load failed (${res.status})`);
      const d = await res.json();
      setData(d);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, token]);

  useEffect(() => { load(); }, [load]);

  // Auto-poll while any profile is in 'generating' state.
  useEffect(() => {
    const isGenerating =
      data?.status_cadence_30 === 'generating' ||
      data?.status_clinic_11 === 'generating';
    if (isGenerating) {
      pollRef.current = setInterval(load, 4000);
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [data?.status_cadence_30, data?.status_clinic_11, load]);

  const triggerGenerate = async (profile) => {
    try {
      setGenerating(profile);
      setBanner(null);
      const res = await fetch(`${API_URL}/api/admin/cases/${caseId}/session-kit/generate`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ time_profile: profile }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body?.detail || 'Kickoff failed');
      setBanner({ kind: 'success', text: `Generation started (${profile}) — auto-refreshing every 4s.` });
      load();
    } catch (err) {
      setBanner({ kind: 'error', text: err.message });
    } finally {
      setGenerating(null);
    }
  };

  const copy = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(
      () => setBanner({ kind: 'success', text: 'Copied to clipboard.' }),
      () => setBanner({ kind: 'error', text: 'Copy failed — long-press to copy manually.' })
    );
  };

  if (loading) return <div className="sk-loading"><Loader2 className="spin" size={28} /><p>Loading session kit…</p></div>;
  if (error) return <div className="sk-error"><AlertCircle size={28} /><p>{error}</p></div>;

  const status = data ? data[`status_${activeProfile.replace('-', '_')}`] : null;
  const agenda = data ? data[`agenda_${activeProfile.replace('-', '_')}`] : null;
  const script = data?.patient_script;
  const isGenerating = status === 'generating' || generating === activeProfile;

  return (
    <div className="session-kit-container" data-testid="session-kit-tab">
      <header className="sk-header">
        <div>
          <h3>Call Agenda &amp; Patient Script</h3>
          <p className="sk-subtitle">
            Defends your time on the call. Cites the released report so talking
            points stay grounded — never generic.
            <span className="sk-credit">Drafted by Claude Sonnet 4.5</span>
          </p>
        </div>
        <div className="sk-profile-toggle" role="tablist">
          {PROFILES.map((p) => (
            <button
              key={p.id}
              role="tab"
              aria-selected={activeProfile === p.id}
              className={`sk-profile-btn ${activeProfile === p.id ? 'active' : ''}`}
              onClick={() => setActiveProfile(p.id)}
              data-testid={`sk-profile-${p.id}`}
            >
              <span className="sk-profile-min">{p.minutes}m</span>
              <span className="sk-profile-label">{p.label}</span>
            </button>
          ))}
        </div>
      </header>

      {banner && (
        <div className={`sk-banner sk-banner-${banner.kind}`}>
          {banner.kind === 'success' ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
          <span>{banner.text}</span>
        </div>
      )}

      <div className="sk-actions">
        <button
          className="sk-btn sk-btn-primary"
          onClick={() => triggerGenerate(activeProfile)}
          disabled={isGenerating}
          data-testid={`sk-generate-${activeProfile}`}
        >
          {isGenerating
            ? <><Loader2 size={16} className="spin" /> Generating ({activeProfile})…</>
            : agenda
              ? <><RotateCw size={16} /> Regenerate</>
              : <><Sparkles size={16} /> Generate Session Kit</>}
        </button>
        {data?.last_generated_at && (
          <span className="sk-last-edited">
            Last generated {new Date(data.last_generated_at).toLocaleString()}
            {data.last_generated_by ? ` · ${data.last_generated_by}` : ''}
          </span>
        )}
      </div>

      {status === 'failed' && data?.last_error && (
        <div className="sk-banner sk-banner-error">
          <AlertCircle size={16} />
          <span>
            <strong>Last attempt failed:</strong> {data.last_error}
            {data.last_error.includes('502')
              ? ' — this is an upstream Anthropic gateway hiccup. Click Regenerate to retry.'
              : ''}
          </span>
        </div>
      )}

      {!agenda && !script && status !== 'generating' && (
        <div className="sk-empty">
          <ScrollText size={32} />
          <h4>No session kit yet</h4>
          <p>
            Click <strong>Generate Session Kit</strong>. This produces TWO artifacts in one go:
            a Patient Script (portal message + sound bytes + talking points) and your
            internal Call Agenda with scope-guards calibrated to defend your time.
          </p>
        </div>
      )}

      {/* AGENDA — internal */}
      {agenda && (
        <section className="sk-agenda" data-testid={`sk-agenda-${activeProfile}`}>
          <div className="sk-section-head">
            <h4><ClipboardCheck size={18} /> Internal Agenda — {PROFILES.find((p) => p.id === activeProfile)?.label}</h4>
            <span className="sk-internal-chip">PATIENT NEVER SEES THIS</span>
          </div>
          {agenda.opening_check && (
            <div className="sk-opening">
              <span className="sk-opening-label">Open with:</span>
              <p>{agenda.opening_check}</p>
            </div>
          )}
          <div className="sk-blocks">
            {(agenda.timed_blocks || []).map((block) => (
              <div key={block.id} className="sk-block">
                <div className="sk-block-head">
                  <Clock size={14} />
                  <span className="sk-block-min">{block.minute_range}</span>
                  <span className="sk-block-label">{block.label}</span>
                </div>
                <ul>
                  {(block.talking_points || []).map((tp, i) => <li key={i}>{tp}</li>)}
                </ul>
              </div>
            ))}
          </div>

          {(agenda.scope_guards || []).length > 0 && (
            <div className="sk-guards">
              <h5><ShieldAlert size={16} /> Scope Guards — pre-written redirects</h5>
              <div className="sk-guards-list">
                {agenda.scope_guards.map((g) => {
                  const meta = SCOPE_TAG_META[g.tag] || SCOPE_TAG_META['time-redirect'];
                  return (
                    <div key={g.id} className={`sk-guard sk-guard-${meta.kind}`}>
                      <div className="sk-guard-head">
                        <span className={`sk-guard-tag sk-guard-tag-${meta.kind}`}>
                          {meta.icon} {meta.label}
                        </span>
                        <p className="sk-guard-q">"{g.trigger_question}"</p>
                      </div>
                      <p className="sk-guard-r"><strong>Say:</strong> {g.redirect}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {(agenda.upsell_hooks || []).length > 0 && (
            <div className="sk-upsells">
              <h5><DollarSign size={16} /> Ethical Upsell Moments</h5>
              {agenda.upsell_hooks.map((u) => (
                <div key={u.id} className="sk-upsell">
                  <div className="sk-upsell-head">
                    <span className="sk-upsell-service">{u.service}</span>
                    <span className="sk-upsell-when">{u.moment}</span>
                  </div>
                  <p>"{u.phrasing}"</p>
                </div>
              ))}
            </div>
          )}

          {(agenda.closing_checklist || []).length > 0 && (
            <div className="sk-closing">
              <h5><ListChecks size={16} /> Close-Out Checklist</h5>
              <ul>{agenda.closing_checklist.map((c, i) => <li key={i}>{c}</li>)}</ul>
            </div>
          )}
        </section>
      )}

      {/* PATIENT SCRIPT — external */}
      {script && (
        <section className="sk-script" data-testid="sk-patient-script">
          <div className="sk-section-head">
            <h4><MessageSquare size={18} /> Patient Script — words to give the patient</h4>
            <span className="sk-external-chip">PATIENT WILL SEE THIS</span>
          </div>

          {script.portal_message && (
            <div className="sk-script-block">
              <div className="sk-script-head">
                <span>Portal Message Draft</span>
                <button className="sk-copy-btn" onClick={() => copy(script.portal_message)} aria-label="Copy">
                  <Copy size={14} /> Copy
                </button>
              </div>
              <p className="sk-script-text">{script.portal_message}</p>
            </div>
          )}

          {(script.virtual_intake_soundbytes || []).length > 0 && (
            <div className="sk-script-block">
              <div className="sk-script-head"><span>Virtual Intake / Triage Sound Bytes</span></div>
              <ul className="sk-soundbytes">
                {script.virtual_intake_soundbytes.map((s, i) => (
                  <li key={i}>
                    <span>{s}</span>
                    <button className="sk-copy-btn-mini" onClick={() => copy(s)} aria-label="Copy"><Copy size={12} /></button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {(script.in_person_talking_points || []).length > 0 && (
            <div className="sk-script-block">
              <div className="sk-script-head"><span>In-Person / Video Talking Points</span></div>
              <ol className="sk-talking-points">
                {script.in_person_talking_points.map((t) => (
                  <li key={t.id}>
                    <p className="sk-tp-ask">{t.ask}</p>
                    {t.anchor && <p className="sk-tp-anchor">Anchored to: {t.anchor}</p>}
                  </li>
                ))}
              </ol>
            </div>
          )}
        </section>
      )}
    </div>
  );
};

export default SessionKitTab;
